import { CellChange, Column, DefaultCellTypes, Row, TextCell } from '@silevis/reactgrid';
import { LcaResultModel } from 'types/types';

export type CellErrorMap = Record<string, string>; // key: `${rowId}:${columnId}`, value: error message

/** Module fields that represent lifecycle stage columns (editable numeric). */
export const MODULE_FIELDS: Array<keyof LcaResultModel> = [
  'a1A2A3',
  'a4',
  'a5',
  'b1',
  'b2',
  'b3',
  'b4',
  'b5',
  'b6',
  'b7',
  'c1',
  'c2',
  'c3',
  'c4',
  'd',
];

/** Column definitions for ReactGrid. */
export function getGridColumns(): Column[] {
  const headerColumns: Column[] = [
    { columnId: 'indicatorName', width: 220 },
    { columnId: 'unit', width: 90 },
  ];

  const moduleColumns: Column[] = MODULE_FIELDS.map((field) => ({
    columnId: field,
    width: 80,
  }));

  return [...headerColumns, ...moduleColumns];
}

/** Header row for ReactGrid. */
export function getHeaderRow(): Row<DefaultCellTypes> {
  const headerCells: DefaultCellTypes[] = [
    { type: 'header', text: 'Indicator', className: 'rg-header-cell' },
    { type: 'header', text: 'Unit', className: 'rg-header-cell' },
    ...MODULE_FIELDS.map((field) => ({
      type: 'header' as const,
      text: formatModuleHeader(field),
      className: 'rg-header-cell',
    })),
  ];

  return {
    rowId: 'header',
    cells: headerCells,
  };
}

/** Convert an array of LcaResultModel into ReactGrid data rows. */
export function getDataRows(rows: LcaResultModel[], cellErrors?: CellErrorMap): Row<DefaultCellTypes>[] {
  return rows.map((row, index) => {
    const rowId = row.indicatorId || `row-${index}`;
    return {
      rowId,
      cells: [
        { type: 'text', text: row.indicatorName || '', nonEditable: true, className: 'rg-cell-noneditable' } as TextCell,
        { type: 'text', text: row.unit || '', nonEditable: true, className: 'rg-cell-noneditable' } as TextCell,
        ...MODULE_FIELDS.map((field) => {
          const errorKey = `${rowId}:${field}`;
          const hasError = cellErrors?.[errorKey];
          return {
            type: 'text',
            text: row[field]?.toString() || '',
            className: hasError ? 'rg-cell-error' : undefined,
          } as TextCell;
        }),
      ],
    };
  });
}

/** Apply cell changes from ReactGrid back to the LcaResultModel array. Returns updated rows. */
export function applyChanges(changes: CellChange<DefaultCellTypes>[], rows: LcaResultModel[]): LcaResultModel[] {
  const updatedRows = [...rows];

  for (const change of changes) {
    const rowIndex = updatedRows.findIndex((r) => r.indicatorId === change.rowId);
    if (rowIndex === -1) continue;

    const fieldName = change.columnId as keyof LcaResultModel;
    if (!MODULE_FIELDS.includes(fieldName)) continue;

    const newCell = change.newCell as TextCell;
    const newValue = newCell.text;

    updatedRows[rowIndex] = {
      ...updatedRows[rowIndex],
      [fieldName]: newValue,
    };
  }

  return updatedRows;
}

/** Get the list of changed LcaResultModels (with full row data) for the mutation. */
export function getChangedModels(changes: CellChange<DefaultCellTypes>[], rows: LcaResultModel[]): LcaResultModel[] {
  const changedIndicatorIds = new Set(changes.map((c) => c.rowId as string));
  return rows.filter((r) => changedIndicatorIds.has(r.indicatorId || ''));
}

function formatModuleHeader(field: string): string {
  const mapping: Record<string, string> = {
    a1A2A3: 'A1-A3',
    a4: 'A4',
    a5: 'A5',
    b1: 'B1',
    b2: 'B2',
    b3: 'B3',
    b4: 'B4',
    b5: 'B5',
    b6: 'B6',
    b7: 'B7',
    c1: 'C1',
    c2: 'C2',
    c3: 'C3',
    c4: 'C4',
    d: 'D',
  };
  return mapping[field] || field.toUpperCase();
}
